/**
 * EncuestaForm - Formulario para responder encuesta de 12 preguntas
 * Features:
 * - 12 preguntas de la plantilla
 * - 3 opciones: Sí / Parcialmente / No
 * - Validación de completitud
 * - Barra de progreso
 */
import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, MinusCircle, AlertCircle } from 'lucide-react';
import { encuestasAPI } from '../../api';
import { Button, Loading, Alert } from '../common';
import useAuthStore from '../../store/authStore';

const EncuestaForm = ({ instanciaId, onSubmit, onCancel }) => {
  const { user } = useAuthStore();
  
  // Estado
  const [plantilla, setPlantilla] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Cargar plantilla
  useEffect(() => {
    loadPlantilla();
  }, []);

  const loadPlantilla = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await encuestasAPI.getPlantilla();
      const preguntas = response.data;
      
      setPlantilla(preguntas);
    } catch (err) {
      console.error('Error al cargar plantilla:', err);
      setError(err.message || 'Error al cargar plantilla');
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas({ ...respuestas, [preguntaId]: valor });
  };

  const validateForm = () => {
    // Validar que todas las preguntas tengan respuesta
    const preguntasRespondidas = Object.keys(respuestas).length;
    const totalPreguntas = plantilla.length;
    
    if (preguntasRespondidas < totalPreguntas) {
      setError(`Por favor responde todas las preguntas (${preguntasRespondidas}/${totalPreguntas})`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Guardar respuestas en batch
      const respuestasArray = Object.keys(respuestas).map((preguntaId) => ({
        id_pregunta: parseInt(preguntaId),
        respuesta: respuestas[preguntaId],
      }));

      await encuestasAPI.guardarRespuestasBatch(instanciaId, respuestasArray);

      setSuccess('Respuestas enviadas correctamente');
      
      if (onSubmit) {
        setTimeout(() => onSubmit(), 1000);
      }
    } catch (err) {
      console.error('Error al guardar respuestas:', err);
      setError(err.message || 'Error al guardar respuestas');
    } finally {
      setSaving(false);
    }
  };

  const getProgress = () => {
    const respondidos = Object.keys(respuestas).length;
    const total = plantilla.length;
    return total > 0 ? Math.round((respondidos / total) * 100) : 0;
  };

  const getIconoRespuesta = (valor) => {
    if (valor === 'si') {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (valor === 'parcialmente') {
      return <MinusCircle className="h-5 w-5 text-yellow-600" />;
    } else if (valor === 'no') {
      return <XCircle className="h-5 w-5 text-red-600" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando encuesta..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Barra de progreso */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Progreso de Respuestas
          </span>
          <span className="text-sm font-medium text-primary-600">
            {getProgress()}% ({Object.keys(respuestas).length}/{plantilla.length})
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgress()}%` }}
          />
        </div>
      </div>

      {/* Información de la encuesta */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Instrucciones:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Responde todas las preguntas honestamente</li>
              <li>Selecciona <strong>"Sí"</strong> si tu organización cumple completamente</li>
              <li>Selecciona <strong>"Parcialmente"</strong> si cumple en parte</li>
              <li>Selecciona <strong>"No"</strong> si no cumple o no existe</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Preguntas */}
        <div className="space-y-4">
          {plantilla.map((pregunta, index) => {
            const preguntaId = pregunta.id_pregunta;
            const respuesta = respuestas[preguntaId];

            return (
              <div
                key={preguntaId}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                {/* Pregunta */}
                <div className="mb-4">
                  <label className="block font-medium text-gray-900 mb-1">
                    {index + 1}. {pregunta.pregunta}
                  </label>
                  {pregunta.descripcion && (
                    <p className="text-sm text-gray-500 mt-1">
                      {pregunta.descripcion}
                    </p>
                  )}
                </div>

                {/* Opciones de respuesta */}
                <div className="grid grid-cols-3 gap-3">
                  {/* Sí */}
                  <button
                    type="button"
                    onClick={() => handleRespuestaChange(preguntaId, 'si')}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 transition-all ${
                      respuesta === 'si'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <CheckCircle
                      className={`h-6 w-6 ${
                        respuesta === 'si' ? 'text-green-600' : 'text-gray-400'
                      }`}
                    />
                    <span className="font-medium">Sí</span>
                    {respuesta === 'si' && (
                      <span className="text-xs text-green-600">Cumplimiento total</span>
                    )}
                  </button>

                  {/* Parcialmente */}
                  <button
                    type="button"
                    onClick={() => handleRespuestaChange(preguntaId, 'parcialmente')}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 transition-all ${
                      respuesta === 'parcialmente'
                        ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                        : 'border-gray-300 hover:border-yellow-300 text-gray-700'
                    }`}
                  >
                    <MinusCircle
                      className={`h-6 w-6 ${
                        respuesta === 'parcialmente' ? 'text-yellow-600' : 'text-gray-400'
                      }`}
                    />
                    <span className="font-medium">Parcialmente</span>
                    {respuesta === 'parcialmente' && (
                      <span className="text-xs text-yellow-600">Cumplimiento parcial</span>
                    )}
                  </button>

                  {/* No */}
                  <button
                    type="button"
                    onClick={() => handleRespuestaChange(preguntaId, 'no')}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 transition-all ${
                      respuesta === 'no'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <XCircle
                      className={`h-6 w-6 ${
                        respuesta === 'no' ? 'text-red-600' : 'text-gray-400'
                      }`}
                    />
                    <span className="font-medium">No</span>
                    {respuesta === 'no' && (
                      <span className="text-xs text-red-600">Sin cumplimiento</span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Botones */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
            >
              Cancelar
            </Button>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={saving || getProgress() !== 100}
            loading={saving}
          >
            Enviar Respuestas
          </Button>
        </div>

        {getProgress() !== 100 && (
          <p className="text-sm text-gray-500 text-center">
            Responde todas las preguntas para enviar la encuesta
          </p>
        )}
      </form>
    </div>
  );
};

export default EncuestaForm;
